import React, { useEffect, useRef, ReactNode } from "react";
import { Banner, BannerVariant } from "../widgets/Banner.tsx";
import { useDialog } from "../DialogManager.tsx";

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  closable?: boolean;
  className?: string;
}

interface DialogSubcomponentProps {
  children: ReactNode;
}

interface DialogErrorMessageProps {
  variant?: BannerVariant;
  title: string;
  body?: string;
  visible: boolean;
  onClose?: () => void;
}

const Dialog: React.FC<DialogProps> & {
  Content: React.FC<DialogSubcomponentProps>;
  Actions: React.FC<DialogSubcomponentProps>;
} = ({ open, onClose, title, children, closable = true, className = "" }) => {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && open) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open, onClose]);

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (
      closable &&
      event.target === event.currentTarget &&
      event.type !== "drag"
    ) {
      onClose();
    }
  };

  if (!open) {
    return null;
  }

  return (
    <div
      id="dialog"
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 sm:p-6 ${className}`}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-title"
    >
      <div
        ref={dialogRef}
        className="bg-white rounded-lg shadow-xl max-w-screen-md w-full flex flex-col mx-2 sm:mx-0"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex flex-row justify-between items-center">
          <h2 id="dialog-title" className="text-xl font-semibold text-gray-800">
            {title}
          </h2>
          {closable && (
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
              aria-label="Close dialog"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Content - scrollable if needed */}
        <div className="flex-1">{children}</div>
      </div>
    </div>
  );
};

const DialogErrorMessage: React.FC<DialogErrorMessageProps> = ({
  variant = "error",
  title,
  body,
  visible,
  onClose,
}) => {
  return (
    <Banner
      variant={variant}
      title={title}
      body={body}
      visible={visible}
      onClose={onClose}
      className="mb-4"
    />
  );
};

const DialogContent: React.FC<DialogSubcomponentProps> = ({ children }) => {
  const { error, setError } = useDialog();
  return (
    <div className="px-6 py-4 mr-4 flex flex-col gap-3 text-gray-600 max-h-[45vh] sm:max-h-[45vh] overflow-y-auto">
      {error && (
        <DialogErrorMessage
          title={"Error"}
          body={error.message}
          visible={true}
          onClose={() => setError(null)}
        />
      )}
      {children}
    </div>
  );
};

const DialogActions: React.FC<DialogSubcomponentProps> = ({ children }) => {
  return (
    <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 bottom-0 bg-white rounded-b-lg">
      {children}
    </div>
  );
};

Dialog.Content = DialogContent;
Dialog.Actions = DialogActions;

export { Dialog };
